import { useEffect, useState, useCallback } from "react";
import { AnimatePresence } from "framer-motion";
import {
  Search, Filter, Plus, X, ChevronDown, FileDown, RefreshCw, Menu
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "./contexts/AuthContext";

import Sidebar from "./components/Sidebar";
import PasswordGate from "./components/PasswordGate";
import LoginPage from "./components/LoginPage";
import StatsGrid from "./components/StatsGrid";
import StaffTable from "./components/StaffTable";
import ActivityLog from "./components/ActivityLog";
import StaffModal from "./components/StaffModal";
import DeleteConfirmModal from "./components/DeleteConfirmModal";
import AdminPanel from "./components/AdminPanel";
import QuotaHistory from "./components/QuotaHistory";
import FeedbackDashboard from "./components/FeedbackDashboard";
import SecurityDashboard from "./components/SecurityDashboard";
import StaffProfile from "./components/StaffProfile";
import BulkImport from "./components/BulkImport";
import AnalyticsPanel from "./components/AnalyticsPanel";
import QuotaAnnouncementSettings from "./components/QuotaAnnouncementSettings";
import "@/App.css";

const API = `${window.location.origin}/api`;

const RANKS = ["HEAD ADMIN", "SENIOR ADMIN", "ADMIN", "SENIOR MOD", "MOD", "JRMOD"];
const ACTIVITY_OPTIONS = ["PASSED", "FAILED", "EXEMPT", "."];
const LOA_OPTIONS = ["Not On LOA", "On LOA", "Exempted"];
const STRIKE_OPTIONS = ["None", "Strike 1", "Strike 2", "Strike 3"];

// Axios interceptors
axios.interceptors.request.use((config) => {
  const token = sessionStorage.getItem("apiToken");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const url = error.config?.url || "";
      if (url.includes("/staff/seed") || url.includes("/auth/")) return Promise.reject(error);
      if (sessionStorage.getItem("apiToken")) {
        sessionStorage.clear();
        window.location.reload();
      }
    }
    return Promise.reject(error);
  }
);

function App() {
  const {
    gateUnlocked, isAuthenticated, currentUser, isAdmin, userRole,
    canViewReports, permissions, discordId, unlockGate, login, logout
  } = useAuth();

  // UI state
  const [activeView, setActiveView] = useState("dashboard");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [showFeedbackDashboard, setShowFeedbackDashboard] = useState(false);
  const [showSecurityDashboard, setShowSecurityDashboard] = useState(false);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [showAnnounceSettings, setShowAnnounceSettings] = useState(false);
  const [selectedStaffProfile, setSelectedStaffProfile] = useState(null);

  // Data state
  const [flaggedStaff, setFlaggedStaff] = useState([]);
  const [staff, setStaff] = useState([]);
  const [stats, setStats] = useState(null);
  const [activityLogs, setActivityLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [logsLoading, setLogsLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({ rank: "all", activity: "all", loa_status: "all", strike_status: "all" });
  const [showFilters, setShowFilters] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ open: false, staff: null });

  // Sidebar nav handler
  const handleViewChange = (view) => {
    if (view === "reports") { setShowFeedbackDashboard(true); return; }
    if (view === "analytics") { setShowAnalytics(true); return; }
    if (view === "bulkimport") { setShowBulkImport(true); return; }
    if (view === "admin") { setShowAdminPanel(true); return; }
    if (view === "announce") { setShowAnnounceSettings(true); return; }
    if (view === "security") { setShowSecurityDashboard(true); return; }
    setActiveView(view);
  };

  const handleLogin = (displayName, adminStatus, role, viewReports, perms, dId) => {
    login(displayName, adminStatus, role, viewReports, perms, dId);
    setLoading(true);
    setTimeout(() => seedData(), 300);
  };

  const fetchLogs = useCallback(async () => {
    setLogsLoading(true);
    try {
      const res = await axios.get(`${API}/activity-logs?limit=50`);
      setActivityLogs(res.data);
    } catch {
      toast.error("Failed to load activity logs");
    } finally {
      setLogsLoading(false);
    }
  }, []);

  const fetchFlaggedStaff = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/feedback-reports/flagged-staff`);
      setFlaggedStaff(res.data);
    } catch {
      toast.error("Failed to load flagged staff");
    }
  }, []);

  const fetchData = useCallback(async () => {
    if (!sessionStorage.getItem("apiToken")) return;
    setFetchError(null);
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (filters.rank !== "all") params.append("rank", filters.rank);
      if (filters.activity !== "all") params.append("activity", filters.activity);
      if (filters.loa_status !== "all") params.append("loa_status", filters.loa_status);
      if (filters.strike_status !== "all") params.append("strike_status", filters.strike_status);

      const [staffRes, statsRes] = await Promise.all([
        axios.get(`${API}/staff?${params.toString()}`),
        axios.get(`${API}/staff/stats`),
      ]);
      setStaff(staffRes.data);
      setStats(statsRes.data);
    } catch (error) {
      setFetchError(error.response?.data?.detail || error.message || "Failed to fetch data");
    } finally {
      setLoading(false);
    }
  }, [search, filters]);

  const seedData = useCallback(async () => {
    if (!sessionStorage.getItem("apiToken")) return;
    try {
      await axios.post(`${API}/staff/seed`);
    } catch {
      toast.error("Failed to seed staff data");
    } finally {
      await fetchData();
      fetchLogs();
      fetchFlaggedStaff();
    }
  }, [fetchData, fetchLogs, fetchFlaggedStaff]);

  useEffect(() => {
    if (isAuthenticated) seedData();
  }, [isAuthenticated, seedData]);

  useEffect(() => {
    if (isAuthenticated && !loading) {
      const debounce = setTimeout(() => fetchData(), 300);
      return () => clearTimeout(debounce);
    }
  }, [search, filters, fetchData, loading, isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;
    const interval = setInterval(() => { fetchData(); fetchLogs(); }, 60000);
    return () => clearInterval(interval);
  }, [isAuthenticated, fetchData, fetchLogs]);

  const handleSaveStaff = async (formData) => {
    try {
      if (editingStaff) {
        await axios.put(`${API}/staff/${editingStaff.id}?performed_by=${encodeURIComponent(`${currentUser} (ID: ${discordId})`)}`, formData);
        toast.success("Staff member updated!");
      } else {
        await axios.post(`${API}/staff?performed_by=${encodeURIComponent(`${currentUser} (ID: ${discordId})`)}`, formData);
        toast.success("Staff member added!");
      }
      setModalOpen(false);
      setEditingStaff(null);
      fetchData();
      fetchLogs();
    } catch {
      toast.error("Failed to save staff member");
    }
  };

  const handleDeleteStaff = async () => {
    if (!deleteModal.staff) return;
    try {
      await axios.delete(`${API}/staff/${deleteModal.staff.id}?performed_by=${encodeURIComponent(`${currentUser} (ID: ${discordId})`)}`);
      toast.success("Staff member deleted!");
      setDeleteModal({ open: false, staff: null });
      fetchData();
      fetchLogs();
    } catch {
      toast.error("Failed to delete staff member");
    }
  };

  const handleExportCSV = () => {
    window.open(`${API}/staff/export/csv`, "_blank");
    toast.success("Exporting CSV...");
  };

  const handleResetAll = async () => {
    if (!window.confirm("Reset ALL staff activity statuses and time?")) return;
    try {
      const res = await axios.post(`${API}/staff/reset-all?performed_by=${encodeURIComponent(`${currentUser} (ID: ${discordId})`)}`);
      toast.success(`Reset ${res.data.modified_count} staff members!`);
      fetchData();
      fetchLogs();
    } catch {
      toast.error("Failed to reset staff");
    }
  };

  // ---- Auth gates ----
  if (!gateUnlocked) return <PasswordGate onUnlock={unlockGate} />;
  if (!isAuthenticated) return <LoginPage onSuccess={handleLogin} />;

  const sidebarWidth = sidebarCollapsed ? "md:pl-16" : "md:pl-56";

  return (
    <div className="min-h-[100dvh] tactical-grid" data-testid="main-app">
      <div className="noise-overlay" />

      {/* Mobile hamburger — visible only on small screens */}
      <button
        onClick={() => setMobileSidebarOpen(true)}
        data-testid="mobile-menu-btn"
        aria-label="Open menu"
        className="md:hidden fixed top-3 left-3 z-30 w-10 h-10 flex items-center justify-center bg-zinc-900/90 border border-zinc-800 backdrop-blur-sm text-zinc-200 active:bg-zinc-800"
      >
        <Menu className="w-5 h-5" />
      </button>

      {/* Sidebar */}
      <Sidebar
        activeView={activeView}
        onViewChange={handleViewChange}
        currentUser={currentUser}
        userRole={userRole}
        discordId={discordId}
        permissions={permissions}
        flaggedCount={flaggedStaff.length}
        collapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
        onLogout={logout}
        mobileOpen={mobileSidebarOpen}
        onMobileClose={() => setMobileSidebarOpen(false)}
      />

      {/* Main content */}
      <main className={`${sidebarWidth} transition-[padding] duration-200 min-h-[100dvh]`}>
        <div className="p-4 md:p-6 max-w-[1600px] pt-16 md:pt-6">
          {/* Page Header */}
          <header className="mb-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-mono text-[9px] tracking-[0.3em] mb-1"><span className="text-zinc-500">// </span><span className="text-white">UNION </span><span className="text-[#FFD600]">INTERACTIVE</span></p>
                <h1 className="font-mono text-xl font-bold text-zinc-100 uppercase tracking-wider" data-testid="app-title">
                  {activeView === "dashboard" ? "Staff Dashboard" : "Quota History"}
                </h1>
                <p className="font-mono text-[10px] text-zinc-600 mt-0.5">
                  QUOTA | AMOUNT: 2H 30M
                </p>
              </div>
            </div>
          </header>

          {/* Dashboard View */}
          {activeView === "dashboard" && (
            <>
              <StatsGrid stats={stats} />

              {/* Controls Bar */}
              <div className="surface-card p-3 mb-4">
                <div className="flex flex-wrap items-center gap-3">
                  <div className="relative flex-1 min-w-[200px]">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
                    <input
                      type="text"
                      placeholder="Search name, username, or ID..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="input-field w-full pl-9"
                      data-testid="search-input"
                    />
                  </div>

                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className={`btn-secondary flex items-center gap-1.5 ${showFilters ? "border-[#00E5FF] text-[#00E5FF]" : ""}`}
                    data-testid="filter-toggle-btn"
                  >
                    <Filter className="w-3.5 h-3.5" />
                    FILTERS
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showFilters ? "rotate-180" : ""}`} />
                  </button>

                  <button onClick={handleExportCSV} className="btn-secondary flex items-center gap-1.5" data-testid="export-csv-btn">
                    <FileDown className="w-3.5 h-3.5" />
                    CSV
                  </button>

                  <button onClick={() => { setEditingStaff(null); setModalOpen(true); }} className="btn-primary flex items-center gap-1.5" data-testid="add-staff-btn">
                    <Plus className="w-3.5 h-3.5" />
                    ADD STAFF
                  </button>

                  <button onClick={handleResetAll} className="btn-secondary flex items-center gap-1.5 border-orange-500/30 text-orange-400 hover:border-orange-500/60" data-testid="reset-all-btn">
                    <RefreshCw className="w-3.5 h-3.5" />
                    RESET
                  </button>
                </div>

                {/* Expanded Filters */}
                <AnimatePresence>
                  {showFilters && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-3 mt-3 border-t border-zinc-800">
                      <div>
                        <label className="block font-mono text-[9px] text-zinc-600 uppercase tracking-[0.15em] mb-1.5">Rank</label>
                        <select value={filters.rank} onChange={(e) => setFilters({ ...filters, rank: e.target.value })} className="select-field w-full" data-testid="filter-rank">
                          <option value="all">All Ranks</option>
                          {RANKS.map((r) => <option key={r} value={r}>{r}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block font-mono text-[9px] text-zinc-600 uppercase tracking-[0.15em] mb-1.5">Activity</label>
                        <select value={filters.activity} onChange={(e) => setFilters({ ...filters, activity: e.target.value })} className="select-field w-full" data-testid="filter-activity">
                          <option value="all">All Activity</option>
                          {ACTIVITY_OPTIONS.map((a) => <option key={a} value={a}>{a}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block font-mono text-[9px] text-zinc-600 uppercase tracking-[0.15em] mb-1.5">LOA Status</label>
                        <select value={filters.loa_status} onChange={(e) => setFilters({ ...filters, loa_status: e.target.value })} className="select-field w-full" data-testid="filter-loa">
                          <option value="all">All Status</option>
                          {LOA_OPTIONS.map((l) => <option key={l} value={l}>{l}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block font-mono text-[9px] text-zinc-600 uppercase tracking-[0.15em] mb-1.5">Strike</label>
                        <select value={filters.strike_status} onChange={(e) => setFilters({ ...filters, strike_status: e.target.value })} className="select-field w-full" data-testid="filter-strike">
                          <option value="all">All Strikes</option>
                          {STRIKE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>
                      {(filters.rank !== "all" || filters.activity !== "all" || filters.loa_status !== "all" || filters.strike_status !== "all" || search) && (
                        <div className="col-span-full flex justify-end">
                          <button
                            onClick={() => { setFilters({ rank: "all", activity: "all", loa_status: "all", strike_status: "all" }); setSearch(""); }}
                            className="btn-secondary flex items-center gap-1.5 border-orange-500/30 text-orange-400"
                            data-testid="reset-filters-btn"
                          >
                            <X className="w-3.5 h-3.5" />
                            CLEAR FILTERS
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </AnimatePresence>
              </div>

              {/* Staff Table */}
              <StaffTable
                staff={staff}
                loading={loading}
                fetchError={fetchError}
                flaggedStaff={flaggedStaff}
                onEdit={(member) => { setEditingStaff(member); setModalOpen(true); }}
                onDelete={(member) => setDeleteModal({ open: true, staff: member })}
                onViewProfile={(member) => setSelectedStaffProfile({ id: member.id, name: member.name })}
                onRetry={() => { setLoading(true); seedData(); }}
                onStaffUpdated={(updated) => {
                  setStaff((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
                }}
              />

              {/* Activity Log */}
              <ActivityLog logs={activityLogs} onRefresh={fetchLogs} loading={logsLoading} />
            </>
          )}

          {/* Quota View */}
          {activeView === "quota" && (
            <QuotaHistory
              currentUser={currentUser}
              currentDiscordId={discordId}
              onViewStaffProfile={(id, name) => setSelectedStaffProfile({ id, name })}
            />
          )}

          {/* Footer */}
          <footer className="mt-8 text-center">
            <p className="font-mono text-[9px] tracking-wider">
              <span className="text-white">UNION </span><span className="text-[#FFD600]">INTERACTIVE</span><span className="text-zinc-700"> STAFF TEAM &copy; {new Date().getFullYear()}</span>
            </p>
          </footer>
        </div>
      </main>

      {/* Modals */}
      <StaffModal
        isOpen={modalOpen}
        onClose={() => { setModalOpen(false); setEditingStaff(null); }}
        staff={editingStaff}
        onSave={handleSaveStaff}
        isEditing={!!editingStaff}
      />

      <DeleteConfirmModal
        isOpen={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, staff: null })}
        onConfirm={handleDeleteStaff}
        staffName={deleteModal.staff?.name}
      />

      <AdminPanel
        isOpen={showAdminPanel}
        onClose={() => setShowAdminPanel(false)}
        currentUser={currentUser}
        currentDiscordId={discordId}
      />

      <AnimatePresence>
        {showFeedbackDashboard && (
          <FeedbackDashboard
            currentUser={currentUser}
            isAdmin={isAdmin}
            permissions={permissions}
            discordId={discordId}
            onClose={() => { setShowFeedbackDashboard(false); fetchFlaggedStaff(); }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showSecurityDashboard && (
          <SecurityDashboard
            isOpen={showSecurityDashboard}
            onClose={() => setShowSecurityDashboard(false)}
          />
        )}
      </AnimatePresence>

      <QuotaAnnouncementSettings
        isOpen={showAnnounceSettings}
        onClose={() => setShowAnnounceSettings(false)}
      />

      <AnimatePresence>
        {selectedStaffProfile && (
          <StaffProfile
            staffId={selectedStaffProfile.id}
            staffName={selectedStaffProfile.name}
            onClose={() => setSelectedStaffProfile(null)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showBulkImport && (
          <BulkImport
            onClose={() => setShowBulkImport(false)}
            onImported={() => fetchData()}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showAnalytics && (
          <AnalyticsPanel
            isOpen={showAnalytics}
            onClose={() => setShowAnalytics(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

export default App;
